'use client'

import Link, { useLinkStatus } from 'next/link'
import { type ComponentPropsWithRef, type MouseEvent, type ReactNode, useEffect } from 'react'

import { useNavigationActions } from '@/components/providers/NavigationProvider'
import { type AnalyticsEventType } from '@/defs/analytics'
import { type DistributiveOmit } from '@/defs/misc'
import { type ScrollType } from '@/defs/scroll'
import { trackEvent } from '@/utils/analyticsUtil'
import { isModifiedClick } from '@/utils/commonUtil'

type LinkRouteProps = {
  analyticsEvent?: AnalyticsEventType
  children: ReactNode
  disabled?: boolean
  hash?: string
  href?: string
  newTab?: boolean
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void
  scroll?: ScrollType
}

type Props = LinkRouteProps & DistributiveOmit<ComponentPropsWithRef<'a'>, keyof LinkRouteProps>

// Records the parent Link as navigating for as long as it is pending
const LinkNavigationRecorder = () => {
  const { pending } = useLinkStatus()
  const { recordPendingNavigation } = useNavigationActions()

  useEffect(() => {
    if (!pending) return

    return recordPendingNavigation()
  }, [recordPendingNavigation, pending])

  return null
}

export const LinkRoute = ({
  analyticsEvent,
  children,
  disabled,
  hash,
  href: hrefProp,
  newTab,
  onClick: onClickProp,
  scroll,
  ...props
}: Props) => {
  const { setScrollRequest } = useNavigationActions()

  const href = `${hrefProp || ''}${hash ? `#${hash}` : ''}`

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClickProp?.(e)
    if (e.defaultPrevented) return
    if (analyticsEvent) trackEvent({ ...analyticsEvent, label: analyticsEvent.label || href })
    if (isModifiedClick(e)) return
    setScrollRequest(href, scroll)
  }

  if (disabled || !href) {
    return (
      <a style={{ cursor: 'default' }} {...props} aria-disabled='true' role='link' tabIndex={0}>
        {children}
      </a>
    )
  }

  const optionalProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  return (
    <Link href={href} onClick={onClick} prefetch={false} scroll={false} {...optionalProps} {...props}>
      <LinkNavigationRecorder />
      {children}
    </Link>
  )
}
